import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SearchField } from "@/components/app/patterns/search-field";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Plus, TrendingDown, Wallet } from "@/lib/icons";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/app/patterns/data-table";
import { OverflowText } from "@/components/app/lists/overflow-text";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc, type orpcClient, type Output } from "@/lib/orpc";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { KEEP_PLACE, optionalSearch } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { formatMoney, parseMoney } from "./-components/financials/money";
import { FUNDING_SOURCE_LABELS } from "./-components/billing/trust-fields";
import { TrustFundDialog } from "./-components/billing/trust-fund-dialog";
import {
  formatTxnDate,
  TrustTransactionActions,
} from "./-components/billing/trust-transaction-actions";
import { useZone } from "@/lib/facility/zone";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/trust-fund")({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: optionalSearch(search.q),
  }),
  component: ResidentTrustFundTab,
});

const TYPE_LABEL: Record<string, string> = {
  deposit: "Deposit",
  invoice_application: "Invoice applied",
  disbursement: "Disbursement",
};

type Txn = Output<typeof orpcClient.financials.trust.get>["transactions"][number];
const MONEY = { align: "right", nowrap: true, className: "tabular-nums font-medium" } as const;

function trustColumns(zone: string): ColumnDef<Txn>[] {
  return [
    {
      id: "date",
      accessorFn: (row) => new Date(row.transactionDate).getTime(),
      header: "Date",
      meta: { nowrap: true },
      cell: ({ row }) => formatTxnDate(row.original.transactionDate, zone),
    },
    {
      id: "type",
      accessorFn: (row) => TYPE_LABEL[row.transactionType] ?? row.transactionType,
      header: "Type",
      meta: { filter: "select" },
    },
    {
      accessorKey: "description",
      header: "Description",
      enableHiding: false,
      cell: ({ row }) => (
        <>
          <OverflowText width="lg">{row.original.description}</OverflowText>
          {row.original.notes ? (
            <OverflowText width="lg" className="text-muted-foreground text-xs">
              {row.original.notes}
            </OverflowText>
          ) : null}
        </>
      ),
    },
    {
      id: "source",
      accessorFn: (row) =>
        row.fundingSource
          ? (FUNDING_SOURCE_LABELS[row.fundingSource as keyof typeof FUNDING_SOURCE_LABELS] ??
            row.fundingSource)
          : "-",
      header: "Source",
      meta: { priority: 2, className: "text-muted-foreground" },
      cell: ({ row, getValue }) => (
        <>
          {String(getValue())}
          {row.original.ssaSentToAccount ? (
            <div className="text-xs">SSA sent to {row.original.ssaSentToAccount}</div>
          ) : null}
        </>
      ),
    },
    {
      accessorFn: (row) => row.referenceNumber || "-",
      id: "reference",
      header: "Reference",
      meta: { priority: 3, className: "text-muted-foreground", clamp: "md", lines: 1 },
    },
    {
      id: "amount",
      accessorFn: (row) => parseMoney(row.amount),
      header: "Amount",
      meta: MONEY,
      cell: ({ row }) => {
        const debit = row.original.transactionType !== "deposit";
        return (
          <span className={debit ? "text-destructive" : "text-ok"}>
            {debit ? "-" : "+"}
            {formatMoney(Math.abs(parseMoney(row.original.amount)))}
          </span>
        );
      },
    },
    { accessorKey: "balanceAfter", header: "Balance", meta: MONEY },
  ];
}

function ResidentTrustFundTab() {
  const zone = useZone();
  const { residentId } = Route.useParams();
  const [recording, setRecording] = useState<"deposit" | "disburse" | null>(null);
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { text: search, change: setSearch } = useSearchParamText(
    params.q,
    (q) => void navigate({ search: { q }, ...KEEP_PLACE }),
  );
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canManage = useCan("financial:manage", resident.facilityId);
  const trust = useQuery(orpc.financials.trust.get.queryOptions({ input: { residentId } }));

  const balanceText = trust.data?.account.balance ?? "$0.00";
  const balance = parseMoney(balanceText);
  const needle = search.trim().toLowerCase();
  const rows = (trust.data?.transactions ?? []).filter(
    (row) =>
      !needle ||
      [
        row.description,
        row.notes,
        row.referenceNumber,
        row.amount,
        row.fundingSource,
        row.ssaSentToAccount,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle),
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 flex size-10 items-center justify-center rounded-md">
                <Wallet className="text-primary size-5" />
              </div>
              <div>
                <CardTitle>Trust fund account</CardTitle>
                <p className="text-muted-foreground mt-0.5 text-sm">
                  Manage resident trust fund deposits and withdrawals
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-muted-foreground text-sm">Current balance</div>
              {trust.isPending ? (
                <Skeleton className="mt-1 h-8 w-32" />
              ) : trust.isError ? (
                /* Not "$0.00": the ledger below says what failed and retries. */
                <div className="text-muted-foreground mt-1 text-sm">Balance unavailable</div>
              ) : (
                <div className="text-2xl font-semibold tabular-nums">{balanceText}</div>
              )}
            </div>
          </div>
        </CardHeader>
        {canManage ? (
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={balance <= 0}
                onClick={() => setRecording("disburse")}
              >
                <TrendingDown className="size-4" />
                Disburse to resident
              </Button>
              <Button onClick={() => setRecording("deposit")}>
                <Plus className="size-4" />
                Deposit
              </Button>
            </div>
          </CardContent>
        ) : null}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Transaction ledger</CardTitle>
        </CardHeader>
        <CardContent>
          {trust.isPending ? (
            <div className="space-y-3">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          ) : trust.isError ? (
            <ReadFailed
              message="Could not load the trust fund ledger."
              error={trust.error}
              onRetry={() => void trust.refetch()}
              retrying={trust.isFetching}
            />
          ) : !trust.data?.transactions.length ? (
            <EmptyState
              title="No trust fund transactions"
              description="Deposits, invoice applications, and disbursements will appear here."
            />
          ) : (
            <div className="space-y-4">
              <SearchField value={search} onChange={setSearch} placeholder="Search transactions…" />
              <DataTable
                flush
                quiet
                data={rows}
                columns={trustColumns(zone)}
                noun="transactions"
                rowId={(row) => row.id}
                empty={
                  <p className="text-muted-foreground px-4 py-6 text-center text-sm">
                    No transactions found
                  </p>
                }
                renderRowActions={
                  canManage
                    ? (row) => <TrustTransactionActions residentId={residentId} transaction={row} />
                    : undefined
                }
              />
            </div>
          )}
        </CardContent>
      </Card>

      <TrustFundDialog
        residentId={residentId}
        kind={recording}
        balance={balanceText}
        onClose={() => setRecording(null)}
      />
    </div>
  );
}
